import type { JSX } from "solid-js";
import { ArchiveTray, WarningCircle, Info, SpinnerGap } from "phosphor-solid";
import { Icon, type PhosphorIcon } from "@/components/ui/Icon";
import { cn } from "@/lib/cn";

type StateVariant = "loading" | "error" | "empty" | "info";
type StateSize = "default" | "compact";

const variantIcon: Record<StateVariant, PhosphorIcon> = {
  loading: SpinnerGap,
  error: WarningCircle,
  empty: ArchiveTray,
  info: Info,
};

const variantClass: Record<StateVariant, string> = {
  loading: "border-border bg-card text-muted-foreground",
  error: "border-destructive/30 bg-destructive/5 text-destructive",
  empty: "border-border bg-muted/40 text-muted-foreground",
  info: "border-border bg-card text-muted-foreground",
};

export interface StateBlockProps {
  variant?: StateVariant;
  size?: StateSize;
  title?: string;
  children: JSX.Element;
  class?: string;
  role?: JSX.HTMLAttributes<HTMLDivElement>["role"];
  ariaLive?: "off" | "polite" | "assertive";
}

export function StateBlock(props: StateBlockProps) {
  const variant = () => props.variant ?? "info";
  const icon = () => variantIcon[variant()];

  return (
    <div
      role={props.role ?? (variant() === "error" ? "alert" : "status")}
      aria-live={props.ariaLive ?? (variant() === "error" ? "assertive" : "polite")}
      class={cn(
        "flex items-start rounded border text-sm",
        props.size === "compact" ? "gap-2 p-2 text-xs" : "gap-3 p-3",
        variantClass[variant()],
        props.class,
      )}
    >
      <Icon
        as={icon()}
        class={cn("mt-0.5", variant() === "loading" && "animate-spin")}
      />
      <div class="space-y-1">
        {props.title ? <p class="font-medium text-foreground">{props.title}</p> : null}
        <div>{props.children}</div>
      </div>
    </div>
  );
}
